import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { Download, FileText, FileType2, Loader2, RefreshCw } from "lucide-react";
import { downloadMagazinePdf, downloadMagazineWord, type MagazineRow } from "./magazineExport";

interface Row extends MagazineRow {
  id: string;
  kit_event_id: string;
  updated_at?: string;
}

export const AllRegionsMagazineReports: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await (supabase as any)
      .from("kit_magazine_submissions")
      .select("*")
      .eq("status", "submitted")
      .order("submitted_at", { ascending: false });
    setRows((data as Row[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const channel = (supabase as any)
      .channel("kit-magazine-submissions-rt")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "kit_magazine_submissions" },
        () => load(),
      )
      .subscribe();
    return () => { (supabase as any).removeChannel(channel); };
  }, []);

  const filtered = rows.filter(
    (r) =>
      !filter ||
      (r.region || "").toLowerCase().includes(filter.toLowerCase()) ||
      (r.conference_title || "").toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>📚 All Regions — Submitted KIT Magazine Reports</span>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Filter region or title…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-8 w-56"
            />
            <Button size="sm" variant="outline" onClick={load}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No submitted magazine reports yet. Reports appear here as soon as a region submits.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Region</TableHead>
                <TableHead>Conference</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Attendance</TableHead>
                <TableHead className="text-right">Download</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell><Badge variant="outline">{r.region}</Badge></TableCell>
                  <TableCell className="font-medium">{r.conference_title}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell>{r.total_attendance || 0}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => downloadMagazineWord(r)}>
                      <FileType2 className="w-4 h-4 mr-1" /> Word
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadMagazinePdf(r)}>
                      <FileText className="w-4 h-4 mr-1" /> PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
